import { sendAnalytics } from '@audio-network/analytics'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import useProjectStore from '@/stores/projectStore'
import { sendAlgoliaInsightEvents } from '@/utils/algolia/sendAlgoliaInsightEvents'

import * as Styled from '../../Forms/Forms.styles'
import AddToProjectToast from '../AddToProjectToast/AddToProjectToast'

type NewProjectFormData = {
	projectName: string
}

type Props = {
	onClose: () => void
	tracks: Track[]
	source: string
}

export default function AddToProjectsNewForm({
	onClose,
	tracks,
	source,
}: Props): JSX.Element {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<NewProjectFormData>({ mode: 'onChange' })

	const addProject = useProjectStore((state) => state.addProject)
	const addTrackToProject = useProjectStore((state) => state.addTracksToProject)

	const { t } = useTranslation(['addtoproject'])

	const handleNewProjectSubmit: SubmitHandler<NewProjectFormData> = async (
		data
	): Promise<void> => {
		toast.dismiss()

		try {
			const project = await addProject(data.projectName)
			if (!project) {
				throw new Error('Failed to create project')
			}

			await addTrackToProject(
				project.Id,
				tracks.map((track) => track.id)
			)

			toast.success(
				<AddToProjectToast tracks={tracks} projectName={data.projectName} />
			)

			const analyticsOptions =
				tracks.length === 1
					? {
							New: true,
							TrackId: tracks[0].id,
							TrackTitle: tracks[0].title,
							AlbumNo: tracks[0].album.number,
							TrackSource: source,
							Catalogue: tracks[0].album.catalogue.name,
							Genre: tracks[0].album.genre,
					  }
					: {
							New: true,
							TrackCount: tracks.length,
							TrackSource: source,
					  }

			sendAnalytics({
				noun: 'Add to Project',
				pastTenseVerb: 'Completed',
				eventParameters: analyticsOptions,
			})

			sendAlgoliaInsightEvents({
				eventType:
					source === 'Search'
						? 'convertedObjectIDsAfterSearch'
						: 'convertedObjectIDs',
				eventName: 'Add to Project Completed',
				track: tracks[0],
			})
		} catch {
			toast.error(<AddToProjectToast error />)
		} finally {
			onClose()
		}
	}

	return (
		<Styled.Form noValidate onSubmit={handleSubmit(handleNewProjectSubmit)}>
			<Styled.FieldLabel htmlFor="NewProject" hasError={errors.projectName}>
				{t('add-to-new-project.project-field')}
			</Styled.FieldLabel>
			<Styled.FieldWrap hasError={errors.projectName}>
				<Styled.FieldInput
					type="text"
					id="NewProject"
					required
					placeholder={t('add-to-new-project.project-placeholder')}
					error={errors.projectName}
					aria-invalid={errors.projectName ? 'true' : 'false'}
					{...register('projectName', {
						required: true,
					})}
				/>
			</Styled.FieldWrap>
			{errors.projectName && (
				<Styled.ErrorMessage data-testid="error">
					{t('add-to-new-project.project-error')}
				</Styled.ErrorMessage>
			)}
			<Styled.ButtonWrapper>
				<Styled.SubmitButton type="submit" data-testid="accept-add-all">
					{t('add-to-new-project.submit-form')}
				</Styled.SubmitButton>
			</Styled.ButtonWrapper>
		</Styled.Form>
	)
}
